import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import Dialog from "../../base/dialog";
import Button from "../../base/button/button";
import {Box, DialogActions, DialogContent, DialogTitle} from "@mui/material";
import ToastService from "../../../../core/services/toast";
import {CreditCardProviders} from "../../../../core/constants/enums";
import NewCreditCardForm from "../../app-specific/new-credit-card-form";

const CreateCreditCardDialog = ({open, close, onSuccess: onSuccessProps, customerId, providers}) => {
    const [submitting, setSubmitting] = useState(false);
    const [selectedProvider, setSelectedProvider] = useState(providers?.[0]?.name);
    const formRef = useRef();

    /**
     * As soon as the dialog is opened, sets the submitting state to false and selects the first provider.
     * - if there are no providers, shows an error message and closes the dialog.
     */
    useEffect(() => {
        if (open) {
            setSelectedProvider(providers?.[0]?.name);
            setSubmitting(false);
            if (!providers?.length) {
                ToastService.error({
                    title: 'Access Denied',
                    message: 'You do not have access to any payment providers. please contact your administrator.',
                    hideTicketSubmission: false,
                })
                close();
            }
        }
    }, [close, open, providers])

    /**
     * Submits the new credit card form with the option of saving its data.
     * @type {function(): Promise<void>}
     */
    const addCreditCard = useCallback(async () => {
        await formRef.current?.submit(true);
    }, []);

    /**
     * Calls the onSuccess callback of this component and then closes the dialog.
     * @type {(function(*): void)}
     */
    const onSuccess = useCallback((cardInfo) => {
        onSuccessProps(cardInfo);
        close();
    }, [onSuccessProps, close]);

    const maxWidth = useMemo(() => {
        switch (selectedProvider) {
            case CreditCardProviders.moneris:
                return 'md';
            case CreditCardProviders.stripe:
                return 'sm';
            default:
                return 'md';
        }
    }, [selectedProvider])

    return (
        <Dialog
            open={open}
            setOpen={close}
            maxWidth={maxWidth}
            loading={submitting}
        >
            <DialogTitle>
                New Credit Card
            </DialogTitle>
            <DialogContent dividers className={'p-0'}>
                <NewCreditCardForm
                    onSuccess={onSuccess}
                    customerId={customerId}
                    setSubmitting={setSubmitting}
                    providers={providers}
                    setSelectedProvider={setSelectedProvider}
                    ref={formRef}
                    className={'p-3'}
                />
            </DialogContent>
            <DialogActions className={'p-0'}>
                <Box sx={{margin: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%'}}>
                    <Button
                        disabled={submitting}
                        variant={'dialog'}
                        color={'primary'}
                        onClick={() => close()}
                        borderRadius={'0 8px 0 8px'}
                    >
                        Close
                    </Button>
                    <Button
                        disabled={submitting}
                        variant={'dialog'}
                        color={'primary'}
                        onClick={addCreditCard}
                        borderRadius={'8px 0 8px 0'}
                    >
                        {submitting ? "Adding..." : 'Add'}
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>
    );
}

export default CreateCreditCardDialog;
